import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { isClassSubscriptionIncluded, showToast } from '../../helpers';
import {
  getCartFromStorage,
  removeCartFromStorage,
  saveCart,
} from '../../helpers/localStorage';
import { IUser, UserRole } from '../../models/IUser';
import {
  TAssignmentUpdate,
  TCart,
  TCheckoutStatus,
  TCouponValidation,
  TInitialCheckoutResult,
  TPrivateCartItem,
  TPublicCartItem,
} from '../../types';
import { fetchUser } from './actionCreators';

interface UserState {
  user: IUser | null;
  isLoading: boolean;
  isManager: boolean;
  isCartOpen: boolean;
  isProgramMessageShown: boolean;
  cart: TCart;
  cartTotal: number;
  cartTotalToPay: number;
}

const initialState: UserState = {
  user: null,
  isLoading: true,
  isManager: false,
  isCartOpen: false,
  isProgramMessageShown: true,
  cart: {
    public: [],
    private: [],
    processed: [],
    status: 'open',
    createdAt: new Date().getTime(),
  },
  cartTotal: 0,
  cartTotalToPay: 0,
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUserState(state) {
      state = initialState;
      return state;
    },
    addPublicCourse(state, { payload }: PayloadAction<TPublicCartItem>) {
      if (!payload.price) {
        showToast(
          'This class is not available due to a missing price.',
          'error',
        );
        return;
      }
      if (state.cart.public.some((item) => item.id === payload.id)) {
        return;
      }
      state.cart.public.push(payload);
      calculateCartTotal(state);
      saveCart(state.cart);
      state.isCartOpen = true;
    },
    removePublicCourse(state, { payload }: PayloadAction<TPublicCartItem>) {
      state.cart.public = state.cart.public.filter(
        (item) => item.id !== payload.id,
      );
      calculateCartTotal(state);
      saveCart(state.cart);
    },
    togglePublicCourse(state, { payload }: PayloadAction<TPublicCartItem>) {
      if (state.cart.public.some((item) => item.id === payload.id)) {
        state.cart.public = state.cart.public.filter(
          (item) => item.id !== payload.id,
        );
      } else {
        if (!payload.price) {
          showToast(
            'This class is not available due to a missing price.',
            'error',
          );
          return;
        }
        state.cart.public.push(payload);
      }
      calculateCartTotal(state);
      saveCart(state.cart);
      state.isCartOpen = true;
    },
    toggleClassAssignment(
      state,
      { payload }: PayloadAction<TAssignmentUpdate>,
    ) {
      const { itemId, studentId } = payload;
      const item = state.cart.public.find((item) => item.id === itemId);
      if (!item) {
        return;
      }
      if (!item.studentIds) {
        item.studentIds = [studentId];
      } else if (item.studentIds.includes(studentId)) {
        item.studentIds = item.studentIds.filter((id) => id !== studentId);
      } else {
        item.studentIds.push(studentId);
      }
      state.cart.public = state.cart.public.map((item) =>
        item.id === itemId ? { ...item } : item,
      );
      calculateCartTotal(state);
      saveCart(state.cart);
    },
    removeStudentAssignment(state, { payload }: PayloadAction<string>) {
      state.cart.public = state.cart.public.map((item) => {
        item.studentIds = item.studentIds?.filter((id) => id !== payload);
        return { ...item };
      });
      calculateCartTotal(state);
      saveCart(state.cart);
    },
    addPrivateCourse(state, { payload }: PayloadAction<TPrivateCartItem>) {
      if (state.cart.private.some((item) => item.id === payload.id)) {
        return;
      }
      state.cart.private.push(payload);
      saveCart(state.cart);
    },
    removePrivateCourse(state, { payload }: PayloadAction<TPrivateCartItem>) {
      state.cart.private = state.cart.private.filter(
        (item) => item.id !== payload.id,
      );
      saveCart(state.cart);
    },
    togglePrivateCourse(state, { payload }: PayloadAction<TPrivateCartItem>) {
      if (state.cart.private.some((item) => item.id === payload.id)) {
        state.cart.private = state.cart.private.filter(
          (item) => item.id !== payload.id,
        );
      } else {
        state.cart.private.push(payload);
      }
      saveCart(state.cart);
      state.isCartOpen = true;
    },
    setIsCartOpen(state, action: PayloadAction<boolean>) {
      state.isCartOpen = action.payload;
    },
    setIsProgramMessageShown(state, action: PayloadAction<boolean>) {
      state.isProgramMessageShown = action.payload;
    },
    setActiveCoupon(
      state,
      action: PayloadAction<TCouponValidation | undefined>,
    ) {
      if (action.payload) {
        state.cart.coupon = {
          code: action.payload.metadata.couponCode,
          percentOff: action.payload.metadata.percentOff,
          amountOff: action.payload.metadata.amountOff,
        };
      } else {
        state.cart.coupon = undefined;
      }
      saveCart(state.cart);
    },
    setCheckoutStatus(state, action: PayloadAction<TCheckoutStatus>) {
      state.cart.status = action.payload;
      saveCart(state.cart);
    },
    clearCart(state) {
      removeCartFromStorage();
      state.cart.status = 'open';
      state.cart.private = [];
      state.cart.public = [];
      state.cart.processed = [];
      state.cart.coupon = undefined;
      calculateCartTotal(state);
    },
    completeFirstCheckoutStep(
      state,
      action: PayloadAction<TInitialCheckoutResult>,
    ) {
      state.cart.processed = action.payload.processedItems;
      if (!action.payload.itemsToPay.length) {
        state.cart.status = 'completed';
        return;
      }

      state.cart.status = 'second_step';
      state.cart.private = [];
      state.cart.public = action.payload.itemsToPay;
      calculateCartTotal(state);
      saveCart(state.cart);
    },
    setCompanyLogo(state, action: PayloadAction<string>) {
      if (state.user) {
        state.user.company.photo = action.payload;
      }
    },
    setUserAvatar(state, action: PayloadAction<string>) {
      if (state.user) {
        state.user.photo = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isManager = action.payload.role === UserRole.MANAGER;
        // reset cart properties  on login, including assigned students (quantity)
        state.cart.public = state.cart.public.map((item) => {
          item.studentIds = undefined;
          item.included = isClassSubscriptionIncluded(
            item.endTime,
            state.user?.program,
          );
          return item;
        });
        calculateCartTotal(state);
        saveCart(state.cart);
      })
      .addCase(fetchUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
      })
      .addCase(fetchUser.pending, (state) => {
        state.cart = getCartFromStorage() ?? state.cart;
        calculateCartTotal(state);
      });
  },
});

// Calculates cart totals. cartTotal = full price of all public items.
// cartTotalToPay = only items NOT covered by the user's program (included === false).
// When cartTotalToPay === 0, checkout skips Stripe and books directly via POST /api/checkout.
// Private classes are not counted here — they are billed through the company's opportunity.
const calculateCartTotal = (state: UserState) => {
  state.cartTotal = 0;
  state.cartTotalToPay = 0;
  state.cart.public.forEach(({ price, studentIds, included }) => {
    if (!price) {
      console.error('Price is missing for a public course');
      return;
    }
    state.cartTotal += price * (studentIds?.length || 1);
    if (!included) {
      state.cartTotalToPay += price * (studentIds?.length || 1);
    }
  });
};

export const {
  clearUserState,
  completeFirstCheckoutStep,
  addPublicCourse,
  removePublicCourse,
  togglePublicCourse,
  addPrivateCourse,
  removePrivateCourse,
  togglePrivateCourse,
  toggleClassAssignment,
  removeStudentAssignment,
  setIsCartOpen,
  setIsProgramMessageShown,
  setActiveCoupon,
  clearCart,
  setCheckoutStatus,
  setCompanyLogo,
  setUserAvatar,
} = userSlice.actions;
export default userSlice.reducer;
